/**
 * ActiveSessionsPanel
 * Table showing currently active charging sessions
 */

import { Battery, Clock, Zap, MapPin } from 'lucide-react';
import { Skeleton } from '@/core/components/ui/skeleton';
import { ActiveSession } from '../../types/dashboard-types';
import { ConnectorIcon } from '../atoms/ConnectorIcon';
import { cn } from '@/core/lib/utils';
import { GRID_STYLES } from '@/core/components/ui/grid-styles';

interface ActiveSessionsPanelProps {
  sessions: ActiveSession[];
  isLoading?: boolean;
  error?: string | null;
}

function formatDuration(minutes: number): string {
  if (minutes < 60) {
    return `${minutes}m`;
  }
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours}h ${mins}m`;
}

export function ActiveSessionsPanel({
  sessions,
  isLoading = false,
  error,
}: ActiveSessionsPanelProps) {
  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
        <p className="font-medium">Error loading live sessions</p>
        <p className="text-sm mt-1">{error}</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-16 w-full" />
        ))}
      </div>
    );
  }

  if (sessions.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <Battery className="h-12 w-12 mx-auto mb-4 text-gray-300" />
        <p className="text-lg font-medium">No Active Sessions</p>
        <p className="text-sm mt-1">All charging stations are currently available</p>
      </div>
    );
  }

  return (
    <div className={cn('overflow-hidden border rounded-lg', GRID_STYLES.border)}>
      <table className="min-w-full">
        <thead className={GRID_STYLES.header.background}>
          <tr>
            <th className={cn(GRID_STYLES.header.cell, GRID_STYLES.header.text)}>
              Session ID
            </th>
            <th className={cn(GRID_STYLES.header.cell, GRID_STYLES.header.text)}>
              Charging Station
            </th>
            <th className={cn(GRID_STYLES.header.cell, GRID_STYLES.header.text)}>
              Location
            </th>
            <th className={cn(GRID_STYLES.header.cell, GRID_STYLES.header.text)}>
              Connector
            </th>
            <th className={cn(GRID_STYLES.header.cell, GRID_STYLES.header.text)}>
              Duration
            </th>
            <th className={cn(GRID_STYLES.header.cell, GRID_STYLES.header.text)}>
              Energy
            </th>
            <th className={cn(GRID_STYLES.header.cell, GRID_STYLES.header.text)}>
              Power
            </th>
            <th className={cn(GRID_STYLES.header.cell, GRID_STYLES.header.text)}>
              Account
            </th>
          </tr>
        </thead>
        <tbody className="bg-white">
          {sessions.map((session) => (
            <tr key={session.sessionId} className={cn(GRID_STYLES.row.hover, `border-b ${GRID_STYLES.border}`)}>
              <td className="px-4 py-3 whitespace-nowrap">
                <span className="text-sm text-gray-900">{session.sessionId}</span>
              </td>
              <td className="px-4 py-3 whitespace-nowrap">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                  <div>
                    <div className="text-sm font-medium text-gray-900">{session.machineName}</div>
                    {session.machineNumber && (
                      <div className="text-xs text-gray-400">{session.machineNumber}</div>
                    )}
                  </div>
                </div>
              </td>
              <td className="px-4 py-3 whitespace-nowrap">
                <div className="flex items-center gap-1 text-sm text-gray-600">
                  <MapPin className="h-3 w-3" />
                  {session.locationName}
                </div>
              </td>
              <td className="px-4 py-3 whitespace-nowrap">
                <div className="flex items-center gap-1">
                  <ConnectorIcon status="Charging" type={session.connectorType} size="sm" />
                  <span className="text-sm text-gray-600">{session.connectorType}</span>
                </div>
              </td>
              <td className="px-4 py-3 whitespace-nowrap">
                <div className="flex items-center gap-1 text-sm text-gray-600">
                  <Clock className="h-3 w-3" />
                  {formatDuration(session.durationMinutes)}
                </div>
              </td>
              <td className="px-4 py-3 whitespace-nowrap">
                <div className="flex items-center gap-1 text-sm text-gray-900">
                  <Battery className="h-3 w-3 text-blue-500" />
                  {(session.energyKwh || 0).toFixed(1)} kWh
                </div>
              </td>
              <td className="px-4 py-3 whitespace-nowrap">
                <div className="flex items-center gap-1 text-sm text-gray-900">
                  <Zap className="h-3 w-3 text-yellow-500" />
                  {(session.currentPowerKw || 0).toFixed(1)} kW
                </div>
              </td>
              <td className="px-4 py-3 whitespace-nowrap">
                <div className="text-sm text-gray-600">
                  {session.customerName || '-'}
                  {session.idToken && (
                    <div className="text-xs text-gray-400">{session.idToken}</div>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
